/**
 * `SignalChips` — значки типов проблем хозяйства с числом.
 *
 * API (общий для Обзора, Хозяйств, Хозяйства, Контуров):
 *   <SignalChips counts={SignalCounts} size="sm" | "md" />
 *   - `counts` — из `signalCounts(situation, gapM3?)` (`problems/model.ts`) или
 *     собранные страницей: `deadlines`, `waterM3` | `waterObligations`,
 *     `conflicts`, `quarantine`. `0` и `null` значка НЕ рисуют: ноль — «нет
 *     сигнала», `null` — «не считали»; ни то ни другое не значок.
 *   - `size` — `sm` в строках списков/таблиц, `md` в шапках карточек.
 * Цвет — ADR-0009: сроки и недоподача — `--data-warning` (не красный),
 * вода — `--data-water`, расхождение — нейтраль, карантин — `--data-critical`
 * (критичное состояние объекта). Цвет никогда не один: иконка + число + подпись
 * для чтения с экрана.
 */
import { CalendarClock, Droplets, Scale, ShieldAlert } from 'lucide-react';
import { Icon } from '../../ui/Icon';
import { m3Compact, num, countOf, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import type { SignalCounts } from './model';
import './problems.css';

const m = defineMessages({
  worksNoReport: { id: 'board.signals.worksNoReport', defaultMessage: '{count, plural, one {# работа без отчёта} few {# работы без отчёта} many {# работ без отчёта} other {# работы без отчёта}}', description: 'Часть подсказки значка «Сроки»: число работ без отчёта (дополняет площадь)' },
  haText: { id: 'board.signals.haText', defaultMessage: '{value} га', description: 'Текст значка «Сроки» в строке списка: гектары земли с работой после срока; {value} — целое число; очень коротко' },
  deadlinesHa: { id: 'board.signals.deadlinesHa', defaultMessage: 'Сроки: {ha} га земли с работой после срока · {works}', description: 'Подсказка и подпись для диктора значка «Сроки»; {ha} — гектары, {works} — «12 работ без отчёта»' },
  deadlinesWorks: { id: 'board.signals.deadlinesWorks', defaultMessage: 'Сроки: {works}', description: 'Подсказка и подпись для диктора значка «Сроки» без площади; {works} — «12 работ без отчёта»' },
  waterGap: { id: 'board.signals.waterGap', defaultMessage: 'Вода: недоподача {value}', description: 'Подсказка значка «Вода»: недоподача; {value} — объём с единицей («1,2 млн м³»)' },
  waterChipNoSupply: { id: 'board.signals.waterChipNoSupply', defaultMessage: '{count} без подачи', description: 'Текст значка «Вода» в строке списка: число поливов без подачи; коротко' },
  waterNoSupply: { id: 'board.signals.waterNoSupply', defaultMessage: 'Вода: {count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}} без подачи (поле × полив с закрытым окном)', description: 'Подсказка значка «Вода»: число поливов без подачи; # — число' },
  conflicts: { id: 'board.signals.conflicts', defaultMessage: 'Расхождения: {count}', description: 'Подсказка значка «Расхождения»; {count} — число' },
  quarantine: { id: 'board.signals.quarantine', defaultMessage: 'Карантин: {contours}', description: 'Подсказка значка «Карантин»; {contours} — число контуров словом («3 контура»)' },
});

export interface SignalChipsProps {
  counts: Partial<SignalCounts>;
  size?: 'sm' | 'md';
}

export function SignalChips({ counts, size = 'sm' }: SignalChipsProps) {
  const t = useT();
  const chips: { key: string; cls: string; icon: typeof Droplets; text: string; label: string }[] = [];
  if (counts.deadlines) {
    // Земля первой (решения №645, №690): «12 га» — земля с работой после срока,
    // число работ — в подсказке. Без площади — прежнее число работ.
    const works = t(m.worksNoReport, { count: counts.deadlines });
    chips.push(typeof counts.deadlinesHa === 'number' && counts.deadlinesHa > 0
      ? { key: 'd', cls: 'deadline', icon: CalendarClock, text: t(m.haText, { value: num(Math.max(1, Math.round(counts.deadlinesHa))) }), label: t(m.deadlinesHa, { ha: num(Math.round(counts.deadlinesHa)), works }) }
      : { key: 'd', cls: 'deadline', icon: CalendarClock, text: num(counts.deadlines), label: t(m.deadlinesWorks, { works }) });
  }
  if (counts.waterM3) {
    chips.push({ key: 'w', cls: 'water', icon: Droplets, text: `−${m3Compact(counts.waterM3)}`, label: t(m.waterGap, { value: m3Compact(counts.waterM3) }) });
  } else if (counts.waterObligations) {
    // Единица в самом чипе: без неё «💧25» (обязательства) рядом с «💧−2 056 м³»
    // (объём) на другом экране читалось как та же величина (07.10.2026).
    chips.push({ key: 'w', cls: 'water', icon: Droplets, text: t(m.waterChipNoSupply, { count: num(counts.waterObligations) }), label: t(m.waterNoSupply, { count: counts.waterObligations }) });
  }
  if (counts.conflicts) {
    chips.push({ key: 'c', cls: 'conflict', icon: Scale, text: num(counts.conflicts), label: t(m.conflicts, { count: num(counts.conflicts) }) });
  }
  if (counts.quarantine) {
    chips.push({ key: 'q', cls: 'quarantine', icon: ShieldAlert, text: num(counts.quarantine), label: t(m.quarantine, { contours: countOf(counts.quarantine, WORDS.contour) }) });
  }
  if (!chips.length) return null;
  return (
    <span className={`sigchips sigchips--${size}`}>
      {chips.map(c => (
        <span key={c.key} className={`sigchip sigchip--${c.cls}`} title={c.label} aria-label={c.label}>
          <Icon icon={c.icon} size={16} />
          <span aria-hidden>{c.text}</span>
        </span>
      ))}
    </span>
  );
}
